import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type DocumentFragment,
  type DocumentLineItemsQuery,
  type TriageListItemFragment,
  DocumentLineItemsDocument,
} from "@/__generated__/graphql";
import { TableColumnType } from "@/components/@types";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { FieldTable } from "@/components/organisms/Field";
import { useResolvedGridAppearance } from "@/components/organisms/Table/Table.tools";
import { Values } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { DocumentObject } from "@/objects/DocumentObject";
import { JournalEntryObject } from "@/objects/JournalEntryObject";
import { TransactionObject } from "@/objects/TransactionObject";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { Utils } from "@/utils";
import { Typography } from "@/components/ui/typography";

import type {
  TriageDetail,
  TriageResolvedOptions,
  TriageSuggestionStub,
} from "./TriageView.context";
import type { TriageResolutionTab } from "./TriageView.resolution";

type ChartedAccountRef = {
  id?: string | null;
  identifier?: string | null;
  name: string;
};

/** What a built row stands for, so deleting the row can undo that selection. */
export type TriageJournalLineSource =
  | { kind: "bank" }
  | { kind: "document"; identifier: string }
  | { kind: "transaction"; identifier: string }
  | { kind: "suggestion" }
  | { kind: "category" }
  | { kind: "chartedAccount" };

export type TriageJournalEntryLine = JournalEntryLine;

type TaxRef = {
  id?: string | null;
  identifier?: string | null;
  name?: string | null;
};

type JournalEntryLine = {
  description: string;
  /** Before its tax: the tax beside it is booked to the account each of its rates posts to. */
  debit: number | null;
  credit: number | null;
  chartedAccount: ChartedAccountRef;
  tax?: TaxRef | null;
  /** Signed as the line is: on its debit side when it is a debit. */
  amountTax?: number | null;
  /** Converted from a document in another currency: what it was on it, before its tax. */
  amountOriginal?: string | number | null;
  amountOriginalCurrency?: string | null;
  amountOriginalToAmountFx?: string | number | null;
  document?: {
    __typename: "DocumentObject";
    id: string;
    identifier: string;
    name: string;
  } | null;
  source?: TriageJournalLineSource;
  metadata?: {
    _frontend_uuid?: string;
    _manual?: boolean;
  };
};

const TRIAGE_MANUAL_JOURNAL_LINE_METADATA = {
  _manual: true,
} as const;

function isTriageManualJournalLine(
  row: Record<string, unknown>,
): row is JournalEntryLine & { metadata: { _manual: true } } {
  return (
    typeof row.metadata === "object" &&
    row.metadata != null &&
    (row.metadata as { _manual?: boolean })._manual === true
  );
}

function builtJournalLineKey(line: JournalEntryLine, index: number): string {
  return `built-${index}-${line.description}-${line.debit}-${line.credit}-${line.chartedAccount?.identifier ?? line.chartedAccount?.name ?? ""}`;
}

export type TriageJournalSource = {
  amount?: TriageDetail["amount"] | TriageListItemFragment["amount"];
  description?: string | null;
  /** What it was paid in, where its bank account may say none. */
  amountCurrency?: string | null;
  entity?: TriageDetail["entity"] | TriageListItemFragment["entity"];
  financialAccount?:
    | TriageDetail["financialAccount"]
    | TriageListItemFragment["financialAccount"]
    | null;
  chartedAccount?:
    | TriageDetail["chartedAccount"]
    | TriageListItemFragment["chartedAccount"]
    | null;
};

export type TriageJournalResolved = Pick<
  TriageResolvedOptions,
  | "categoryOption"
  | "documentOption"
  | "documentOptions"
  | "chartedAccountOption"
  | "selectedTransactions"
  | "linesSuggestion"
>;

export const EMPTY_TRIAGE_JOURNAL_RESOLVED: TriageJournalResolved = {
  categoryOption: null,
  documentOption: null,
  documentOptions: [],
  chartedAccountOption: null,
  selectedTransactions: [],
  linesSuggestion: null,
};

/** Prefer checked documents; otherwise the focused document suggestion. */
export function journalDocumentsFromSelectionOrFocus({
  documentOption,
  documentOptions,
  focusedDocumentOption,
}: {
  documentOption: TriageJournalResolved["documentOption"];
  documentOptions: TriageJournalResolved["documentOptions"];
  focusedDocumentOption?: TriageJournalResolved["documentOption"];
}): Pick<TriageJournalResolved, "documentOption" | "documentOptions"> {
  if ((documentOptions?.length ?? 0) > 0) {
    return {
      documentOption: documentOption ?? documentOptions[0] ?? null,
      documentOptions,
    };
  }
  if (focusedDocumentOption?.document?.id) {
    return {
      documentOption: focusedDocumentOption,
      documentOptions: [focusedDocumentOption],
    };
  }
  return {
    documentOption: null,
    documentOptions: [],
  };
}

export function journalResolvedForTab(
  tab: TriageResolutionTab,
  resolved: TriageJournalResolved,
): TriageJournalResolved {
  if (tab === "suggestions") {
    return {
      ...EMPTY_TRIAGE_JOURNAL_RESOLVED,
      linesSuggestion: resolved.linesSuggestion ?? null,
    };
  }

  // Documents and peer transactions accumulate across tabs — multiple bank
  // txs can pay one document. Categorize/book stays tab-scoped.
  return {
    categoryOption: tab === "categorize" ? resolved.categoryOption : null,
    documentOption: resolved.documentOption,
    documentOptions: resolved.documentOptions,
    chartedAccountOption:
      tab === "categorize" ? resolved.chartedAccountOption : null,
    selectedTransactions: resolved.selectedTransactions ?? [],
    linesSuggestion: null,
  };
}

type JournalResolved = TriageJournalResolved;

function debitCreditValues(
  amount: number,
  side: "debit" | "credit",
): Pick<JournalEntryLine, "debit" | "credit"> {
  if (side === "debit") {
    return { debit: amount, credit: 0 };
  }
  return { debit: 0, credit: amount };
}

type DocumentLineItem = {
  amount?: number | string | null;
  description?: string | null;
  chartedAccount?: {
    id?: string | null;
    identifier?: string | null;
    name?: string | null;
  } | null;
  document?: NonNullable<JournalEntryLine["document"]> | null;
};

const journalEntryLinesField = TransactionObject.fieldDefinitions.find(
  (field) => field.objectKey === "journalEntryLines",
);
// An entry written by hand: its lines' Tax column is the one a line with tax
// on it is shown with here.
const handWrittenLinesField = JournalEntryObject.fieldDefinitions.find(
  (field) => field.objectKey === "transactions",
);

function triageDocumentIds(resolved: JournalResolved): string[] {
  const ids = new Set<string>();
  for (const option of resolved.documentOptions ?? []) {
    const documentId = option.document?.id ?? option.id;
    if (documentId) {
      ids.add(documentId);
    }
  }
  const focusedDocumentId =
    resolved.documentOption?.document?.id ?? resolved.documentOption?.id;
  if (focusedDocumentId) {
    ids.add(focusedDocumentId);
  }
  return [...ids];
}

function documentRefFromFields(
  document:
    | {
        id?: string | null;
        identifier?: string | null;
        name?: string | null;
        reference?: string | null;
        description?: string | null;
      }
    | null
    | undefined,
): NonNullable<JournalEntryLine["document"]> | null {
  if (!document?.id || !document.identifier) {
    return null;
  }
  return {
    __typename: "DocumentObject",
    id: document.id,
    identifier: document.identifier,
    name: DocumentObject.getTitle(document as DocumentFragment) ?? "Document",
  };
}

/** Loads document line items for the JE group — not embedded on triage fragments. */
export function useTriageDocumentLineItems(resolved: JournalResolved): {
  lineItems: DocumentLineItem[];
  loading: boolean;
} {
  const client = useApolloClient();
  const documentIds = useMemo(
    () => triageDocumentIds(resolved),
    [resolved.documentOption, resolved.documentOptions],
  );
  const documentIdsKey = documentIds.join("|");
  const [lineItems, setLineItems] = useState<DocumentLineItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (documentIds.length === 0) {
      setLineItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      documentIds.map((id) =>
        client.query<DocumentLineItemsQuery>({
          query: DocumentLineItemsDocument,
          variables: { id },
          fetchPolicy: "cache-first",
        }),
      ),
    )
      .then((results) => {
        if (cancelled) {
          return;
        }
        setLineItems(
          results.flatMap((result) =>
            lineItemsFromDocumentNode(result.data?.node ?? {}),
          ),
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, documentIdsKey]);

  return { lineItems, loading };
}

function lineItemsFromDocumentNode(
  node: NonNullable<DocumentLineItemsQuery["node"]> | Record<string, never>,
): DocumentLineItem[] {
  if (node.__typename !== "DocumentObject") {
    return [];
  }
  const document = documentRefFromFields(node);
  const rows = node.lineItems ?? [];
  if (rows.length > 0) {
    return rows.map((lineItem) => ({
      ...lineItem,
      document,
    }));
  }
  const total = node.amountTotal ?? node.amountDue;
  if (total == null || document == null) {
    return [];
  }
  return [
    {
      amount: total,
      description: node.description ?? node.name ?? null,
      chartedAccount: null,
      document,
    },
  ];
}

function syntheticLineItemsFromDocumentOptions(
  resolved: JournalResolved,
): DocumentLineItem[] {
  return (resolved.documentOptions ?? []).flatMap((option) => {
    const document = option.document;
    if (!document?.identifier) {
      return [];
    }
    const embedded =
      "lineItems" in document && Array.isArray(document.lineItems)
        ? document.lineItems
        : [];
    if (embedded.length > 0) {
      const documentRef = documentRefFromFields(document);
      return embedded.map((lineItem) => ({
        ...lineItem,
        document: documentRef,
      }));
    }
    const total = document.amountTotal ?? document.amountDue;
    if (total == null) {
      return [];
    }
    const documentRef = documentRefFromFields(document);
    if (!documentRef) {
      return [];
    }
    return [
      {
        amount: total,
        description: document.description ?? document.name ?? null,
        chartedAccount: null,
        document: documentRef,
      },
    ];
  });
}

function resolveDocumentLineItems(
  resolved: JournalResolved,
  fetched: DocumentLineItem[],
): DocumentLineItem[] {
  if (fetched.length > 0) {
    return fetched;
  }
  return syntheticLineItemsFromDocumentOptions(resolved);
}

function buildBankAccount(triage: TriageJournalSource): ChartedAccountRef {
  const financialAccount = triage.financialAccount;
  const financialChartedAccount =
    financialAccount != null && "chartedAccount" in financialAccount
      ? financialAccount.chartedAccount
      : undefined;
  if (financialChartedAccount?.id || financialChartedAccount?.identifier) {
    return {
      id: financialChartedAccount.id,
      identifier: financialChartedAccount.identifier,
      name: financialChartedAccount.name ?? financialAccount?.name ?? "Bank",
    };
  }

  const bankChartedAccount = triage.chartedAccount;
  if (bankChartedAccount) {
    return {
      id: bankChartedAccount.id,
      identifier: bankChartedAccount.identifier,
      name: bankChartedAccount.name ?? financialAccount?.name ?? "Bank",
    };
  }
  return {
    name: financialAccount?.name ?? "Bank",
  };
}

function buildDocumentItemLines(
  triage: TriageJournalSource,
  lineItems: DocumentLineItem[],
  resolved: JournalResolved,
): JournalEntryLine[] {
  const amount = Number(triage.amount ?? 0);
  if (amount === 0 || lineItems.length === 0) {
    return [];
  }

  const isOutflow = amount < 0;
  const description = triage.description ?? "";
  // A document in another currency than the payment's is paid in the
  // payment's: what it costs in these books is what was paid, its lines
  // shared out of that by their own amounts - as the server books one picked
  // for the payment. Left in its own currency, its lines sat beside the
  // bank's in another and the entry never balanced.
  // What it was paid in: its own currency, its bank account's, or - nothing
  // more particular saying - its business's.
  const paidIn = (
    triage.amountCurrency ??
    (triage.financialAccount && "currency" in triage.financialAccount
      ? triage.financialAccount.currency
      : null) ??
    (triage.entity && "currency" in triage.entity
      ? triage.entity.currency
      : null)
  )?.toUpperCase();
  const currencyOf = new Map(
    [
      ...(resolved.documentOptions ?? []),
      ...(resolved.documentOption ? [resolved.documentOption] : []),
    ].flatMap((option) =>
      option.document?.identifier && option.document.amountCurrency
        ? [
            [
              option.document.identifier,
              option.document.amountCurrency.toUpperCase(),
            ],
          ]
        : [],
    ),
  );
  const isForeign = (lineItem: DocumentLineItem) => {
    const currency = currencyOf.get(lineItem.document?.identifier ?? "");
    return Boolean(paidIn && currency && currency !== paidIn);
  };
  const foreign = lineItems.filter(isForeign);
  const foreignTotal = foreign.reduce(
    (sum, lineItem) => sum + Math.abs(Number(lineItem.amount ?? 0)),
    0,
  );
  // Shared to the cent, the last of them taking what is left.
  let foreignLeft = Math.abs(amount);
  const inPayment = (lineItem: DocumentLineItem, own: number) => {
    if (!isForeign(lineItem) || foreignTotal === 0) return own;
    if (lineItem === foreign[foreign.length - 1]) return foreignLeft;
    const share =
      Math.round(((own * Math.abs(amount)) / foreignTotal) * 100) / 100;
    foreignLeft = Math.round((foreignLeft - share) * 100) / 100;
    return share;
  };

  return lineItems
    .map((lineItem) => {
      const lineAmount = inPayment(
        lineItem,
        Math.abs(Number(lineItem.amount ?? 0)),
      );
      if (lineAmount === 0) {
        return null;
      }
      const chartedAccount = lineItem.chartedAccount;
      const documentIdentifier = lineItem.document?.identifier;
      return {
        // Turned into the payment's currency: what it was on its document.
        ...(isForeign(lineItem)
          ? {
              amountOriginal: Math.abs(Number(lineItem.amount ?? 0)),
              amountOriginalCurrency: currencyOf.get(documentIdentifier ?? ""),
              amountOriginalToAmountFx:
                lineAmount / Math.abs(Number(lineItem.amount ?? 0) || 1),
            }
          : {}),
        description: lineItem.description ?? description,
        chartedAccount: {
          id: chartedAccount?.id,
          identifier: chartedAccount?.identifier,
          name: chartedAccount?.name ?? "Uncoded",
        },
        document: lineItem.document ?? null,
        ...(documentIdentifier
          ? {
              source: {
                kind: "document" as const,
                identifier: documentIdentifier,
              },
            }
          : {}),
        ...debitCreditValues(lineAmount, isOutflow ? "debit" : "credit"),
      };
    })
    .filter((line) => line != null) as JournalEntryLine[];
}

function buildSelectedTransactionPaymentLines(
  selectedTransactions: NonNullable<JournalResolved["selectedTransactions"]>,
  fallbackDescription: string,
  bankSide: "debit" | "credit",
): JournalEntryLine[] {
  return selectedTransactions.flatMap((transaction) => {
    const lineAmount = Math.abs(Number(transaction.amount ?? 0));
    if (lineAmount === 0) {
      return [];
    }
    return [
      {
        description: transaction.description ?? fallbackDescription,
        chartedAccount: {
          id:
            transaction.chartedAccount?.id ?? transaction.financialAccount?.id,
          identifier:
            transaction.chartedAccount?.identifier ??
            transaction.financialAccount?.identifier,
          name:
            transaction.financialAccount?.name ??
            transaction.chartedAccount?.name ??
            "Bank",
        },
        ...(transaction.identifier
          ? {
              source: {
                kind: "transaction" as const,
                identifier: transaction.identifier,
              },
            }
          : {}),
        // Same side as the triage bank leg — additional payments, not offsets.
        ...debitCreditValues(lineAmount, bankSide),
      } satisfies JournalEntryLine,
    ];
  });
}

function buildCategoryBookingLines(
  triage: TriageJournalSource,
  offsetAccount: ChartedAccountRef,
  offsetSource: TriageJournalLineSource,
): JournalEntryLine[] {
  const amount = Number(triage.amount ?? 0);
  const absAmount = Math.abs(amount);
  const description = triage.description ?? "";
  const bankAccount = buildBankAccount(triage);

  const bankSide: "debit" | "credit" = amount >= 0 ? "debit" : "credit";
  const offsetSide: "debit" | "credit" = amount >= 0 ? "credit" : "debit";

  return [
    {
      description,
      chartedAccount: bankAccount,
      source: { kind: "bank" },
      ...debitCreditValues(absAmount, bankSide),
    },
    {
      description,
      chartedAccount: offsetAccount,
      source: offsetSource,
      ...debitCreditValues(absAmount, offsetSide),
    },
  ];
}

export function journalEntryTotals(lines: JournalEntryLine[]) {
  // Each line with the tax on it, as it is booked.
  const totalDebit = lines.reduce(
    (sum, line) =>
      sum + Number(line.debit || 0) + Math.max(Number(line.amountTax || 0), 0),
    0,
  );
  const totalCredit = lines.reduce(
    (sum, line) =>
      sum +
      Number(line.credit || 0) +
      Math.max(-Number(line.amountTax || 0), 0),
    0,
  );
  const amountsMatch = Math.abs(totalDebit - totalCredit) < 0.005;
  const isBalanced =
    lines.length >= 2 && totalDebit > 0 && totalCredit > 0 && amountsMatch;

  return { totalDebit, totalCredit, isBalanced };
}

type SuggestionLine = {
  amount?: string | number;
  description?: string;
  chartedAccount?: {
    identifier?: string | null;
    name?: string | null;
    id?: string | null;
  } | null;
  /** A catalogue account the business has not made yet; accepting makes it. */
  catalogueAccountName?: string | null;
  tax?: TaxRef | null;
  amountTax?: string | number | null;
  amountOriginal?: string | number | null;
  amountOriginalCurrency?: string | null;
  amountOriginalToAmountFx?: string | number | null;
};

export type TriageJournalSettlementContext = {
  settlementEntityIdentifier: string;
  settlementEntityName?: string | null;
  payerEntityName?: string | null;
};

export function buildTriageJournalEntryLines(
  triage: TriageJournalSource,
  resolved: JournalResolved,
  documentLineItems: DocumentLineItem[] = [],
  settlement?: TriageJournalSettlementContext | null,
): JournalEntryLine[] {
  const suggestionLines = resolved.linesSuggestion?.lines;
  if (Array.isArray(suggestionLines) && suggestionLines.length > 0) {
    const bankAccount = buildBankAccount(triage);
    const txnAmount = Number(triage.amount ?? 0);
    // Exactly one line stands for the triage transaction. Duplicate-review and
    // transfer-clearing suggestions put *both* legs on the bank account, so
    // matching per line would mark every row as the bank leg — which is the one
    // row the panel refuses to rename or delete. Prefer the leg that also
    // carries the transaction's amount.
    const rows = suggestionLines as SuggestionLine[];
    const isBankAccountLine = (line: SuggestionLine) =>
      line.chartedAccount?.identifier != null &&
      bankAccount.identifier != null &&
      line.chartedAccount.identifier === bankAccount.identifier;
    const candidates = [
      rows.findIndex(
        (line) =>
          Number(line.amount ?? 0) === txnAmount && isBankAccountLine(line),
      ),
      rows.findIndex((line) => Number(line.amount ?? 0) === txnAmount),
      rows.findIndex(isBankAccountLine),
    ];
    const bankLineIndex = candidates.find((index) => index >= 0) ?? -1;
    const built = rows
      .map((line, index) => {
        const signed = Number(line.amount ?? 0);
        if (!Number.isFinite(signed) || signed === 0) {
          return null;
        }
        const abs = Math.abs(signed);
        const accountIdentifier = line.chartedAccount?.identifier ?? null;
        const lineAccountName =
          line.chartedAccount?.name ??
          (line.catalogueAccountName
            ? `${line.catalogueAccountName} (new account)`
            : null);
        const isBankLine = index === bankLineIndex;
        // Reference cells render null without a Relay id — always synthesize
        // one from the charted-account identifier when GraphQL id is absent.
        const identifier = isBankLine
          ? (bankAccount.identifier ?? accountIdentifier)
          : accountIdentifier;
        const id =
          (isBankLine ? bankAccount.id : null) ??
          (identifier
            ? identifierAndTypenameToId(identifier, "ChartedAccountObject")
            : null);
        const chartedAccount: ChartedAccountRef = {
          id,
          identifier,
          name:
            (isBankLine ? bankAccount.name : null) ||
            lineAccountName ||
            (isBankLine ? "Bank" : "Account"),
        };
        return {
          // The bank leg is the existing transaction — booking never rewrites
          // its description, so preview it under its own name.
          description:
            (isBankLine ? triage.description : null) || line.description || "",
          chartedAccount,
          source: isBankLine
            ? { kind: "bank" as const }
            : { kind: "suggestion" as const },
          ...(signed > 0
            ? { debit: abs, credit: 0 }
            : { debit: 0, credit: abs }),
          ...(line.tax
            ? { tax: line.tax, amountTax: Number(line.amountTax ?? 0) }
            : {}),
          ...(line.amountOriginal != null
            ? {
                amountOriginal: line.amountOriginal,
                amountOriginalCurrency: line.amountOriginalCurrency,
                amountOriginalToAmountFx: line.amountOriginalToAmountFx,
              }
            : {}),
        } as JournalEntryLine;
      })
      .filter((line): line is JournalEntryLine => line != null);
    if (built.length > 0) {
      return built;
    }
  }

  const amount = Number(triage.amount ?? 0);
  const description = triage.description ?? "";
  const bankAccount = buildBankAccount(triage);
  const absAmount = Math.abs(amount);
  const bankSide: "debit" | "credit" = amount >= 0 ? "debit" : "credit";
  const bankTransactionLine: JournalEntryLine = {
    description,
    chartedAccount: bankAccount,
    source: { kind: "bank" },
    ...(absAmount === 0
      ? { debit: 0, credit: 0 }
      : debitCreditValues(absAmount, bankSide)),
  };

  const selectedTransactions = resolved.selectedTransactions ?? [];
  const effectiveDocumentLineItems = resolveDocumentLineItems(
    resolved,
    documentLineItems,
  );
  const documentItemLines =
    effectiveDocumentLineItems.length > 0
      ? buildDocumentItemLines(triage, effectiveDocumentLineItems, resolved)
      : [];

  // Document + selected peer payments: keep the triage bank leg fixed and add
  // peers on the same side so split payments stay unbalanced until they cover
  // the document.
  if (documentItemLines.length > 0 && selectedTransactions.length > 0) {
    const peerPaymentLines = buildSelectedTransactionPaymentLines(
      selectedTransactions,
      description,
      bankSide,
    );
    return [bankTransactionLine, ...peerPaymentLines, ...documentItemLines];
  }

  if (documentItemLines.length > 0) {
    // Keep the triage bank leg at the row amount — document lines sit beside it
    // and the JE stays unbalanced until payments cover the document(s).
    return [bankTransactionLine, ...documentItemLines];
  }

  // Selected transactions alone: add payment legs beside the triage bank line.
  // Do not resize the bank leg to "balance" against the selection.
  if (selectedTransactions.length > 0) {
    const peerPaymentLines = buildSelectedTransactionPaymentLines(
      selectedTransactions,
      description,
      bankSide,
    );
    if (peerPaymentLines.length > 0) {
      return [bankTransactionLine, ...peerPaymentLines];
    }
  }

  const categoryOption = resolved.categoryOption;
  if (categoryOption?.category) {
    const categoryChartedAccount = categoryOption.category.parent;
    const categoryName = categoryOption.category.name;
    const offsetAccount: ChartedAccountRef | null = categoryChartedAccount
      ? {
          id: categoryChartedAccount.id,
          identifier: categoryChartedAccount.identifier,
          name: categoryChartedAccount.name ?? categoryName ?? "Offset",
        }
      : categoryName
        ? { name: categoryName }
        : null;

    if (offsetAccount) {
      const categoryLines = buildCategoryBookingLines(triage, offsetAccount, {
        kind: "category",
      });
      if (
        !settlement?.settlementEntityIdentifier ||
        settlement.settlementEntityIdentifier === triage.entity?.identifier
      ) {
        return categoryLines;
      }
      return applySettlementJournalLines(triage, categoryLines, settlement);
    }
  }

  const bookChartedAccount = resolved.chartedAccountOption?.chartedAccount;
  let baseLines: JournalEntryLine[];
  if (bookChartedAccount?.identifier || bookChartedAccount?.name) {
    baseLines = buildCategoryBookingLines(
      triage,
      {
        id: bookChartedAccount.id,
        identifier: bookChartedAccount.identifier,
        name: bookChartedAccount.name ?? "Offset",
      },
      { kind: "chartedAccount" },
    );
  } else {
    baseLines = [bankTransactionLine];
  }

  if (
    !settlement?.settlementEntityIdentifier ||
    settlement.settlementEntityIdentifier === triage.entity?.identifier
  ) {
    return baseLines;
  }
  return applySettlementJournalLines(triage, baseLines, settlement);
}

function applySettlementJournalLines(
  triage: TriageJournalSource,
  baseLines: JournalEntryLine[],
  settlement: TriageJournalSettlementContext,
): JournalEntryLine[] {
  const payerEntityId = triage.entity?.identifier;
  if (
    !settlement.settlementEntityIdentifier ||
    !payerEntityId ||
    settlement.settlementEntityIdentifier === payerEntityId
  ) {
    return baseLines;
  }

  const settlementAbsAmount = Math.abs(Number(triage.amount ?? 0));
  if (settlementAbsAmount === 0 || baseLines.length < 1) {
    return baseLines;
  }

  const payerName =
    settlement.payerEntityName ?? triage.entity?.name ?? "Payer";
  const settlementName =
    settlement.settlementEntityName ?? settlement.settlementEntityIdentifier;
  const settlementBankSide: "debit" | "credit" =
    Number(triage.amount ?? 0) >= 0 ? "debit" : "credit";
  const offsetSide: "debit" | "credit" =
    settlementBankSide === "debit" ? "credit" : "debit";
  const bankLine = baseLines[0];
  const expenseLine =
    baseLines.find((line) => line.source?.kind !== "bank") ?? null;

  const interEntityPayerLine: JournalEntryLine = {
    description: `Due to ${settlementName}`,
    chartedAccount: {
      name: `${payerName} · Inter-entity`,
    },
    source: { kind: "suggestion" },
    ...debitCreditValues(settlementAbsAmount, offsetSide),
  };

  const settlementExpenseLine: JournalEntryLine = expenseLine
    ? {
        ...expenseLine,
        description: `${settlementName} · ${expenseLine.description || "Expense"}`,
        chartedAccount: {
          ...expenseLine.chartedAccount,
          name: `${settlementName} · ${expenseLine.chartedAccount.name}`,
        },
      }
    : {
        description: `${settlementName} · Expense`,
        chartedAccount: { name: `${settlementName} · (select document)` },
        source: { kind: "suggestion" },
        ...debitCreditValues(settlementAbsAmount, offsetSide),
      };

  const interEntitySettlementLine: JournalEntryLine = {
    description: `Due from ${payerName}`,
    chartedAccount: {
      name: `${settlementName} · Inter-entity`,
    },
    source: { kind: "suggestion" },
    ...debitCreditValues(settlementAbsAmount, settlementBankSide),
  };

  return [
    {
      ...bankLine,
      description: `${payerName} · ${bankLine.description || "Bank"}`,
      chartedAccount: {
        ...bankLine.chartedAccount,
        name: `${payerName} · ${bankLine.chartedAccount.name}`,
      },
    },
    interEntityPayerLine,
    settlementExpenseLine,
    interEntitySettlementLine,
  ];
}

export function buildBookInput({
  transactionIdentifier,
  triage,
  resolved,
  documentLineItems = [],
  settlementEntityIdentifier = null,
  payerTagIdentifiers = [],
  settlementTagIdentifiers = [],
  settlement,
  newCategoryName = null,
}: {
  transactionIdentifier: string;
  triage: TriageJournalSource;
  resolved: JournalResolved;
  documentLineItems?: DocumentLineItem[];
  settlementEntityIdentifier?: string | null;
  payerTagIdentifiers?: string[];
  settlementTagIdentifiers?: string[];
  settlement?: TriageJournalSettlementContext | null;
  /** A category named for it, not made yet: made as it is booked. */
  newCategoryName?: string | null;
}) {
  const payerEntityIdentifier = triage.entity?.identifier ?? undefined;
  const settlementId =
    settlementEntityIdentifier ??
    settlement?.settlementEntityIdentifier ??
    null;
  const documentEntityByIdentifier = new Map<string, string>();
  for (const option of resolved.documentOptions ?? []) {
    const document = option.document as
      | {
          identifier?: string | null;
          entity?: { identifier?: string | null } | null;
        }
      | null
      | undefined;
    if (document?.identifier && document.entity?.identifier) {
      documentEntityByIdentifier.set(
        document.identifier,
        document.entity.identifier,
      );
    }
  }
  // Booking uses real charted accounts only — inter-entity legs are created server-side.
  const journalLines = buildTriageJournalEntryLines(
    triage,
    resolved,
    documentLineItems,
    null,
  );

  const lines = journalLines
    .map((line) => {
      const signed = Number(line.debit || 0) - Number(line.credit || 0);
      const chartedAccountIdentifier = line.chartedAccount?.identifier;
      if (
        !chartedAccountIdentifier ||
        !Number.isFinite(signed) ||
        signed === 0
      ) {
        return null;
      }
      let lineEntityIdentifier = payerEntityIdentifier;
      if (line.source?.kind === "document" && line.source.identifier) {
        const documentEntityIdentifier = documentEntityByIdentifier.get(
          line.source.identifier,
        );
        if (documentEntityIdentifier) {
          lineEntityIdentifier = documentEntityIdentifier;
        }
      } else if (
        settlementId &&
        line.source?.kind !== "bank" &&
        lineEntityIdentifier !== settlementId
      ) {
        lineEntityIdentifier = settlementId;
      }
      return {
        entityIdentifier: lineEntityIdentifier,
        chartedAccountIdentifier,
        amount: String(signed),
        ...(line.tax?.identifier
          ? {
              tax: { identifier: line.tax.identifier },
              amountTax: String(line.amountTax ?? 0),
            }
          : {}),
        // Converted from a document in another currency: what it was on it,
        // on the line's own side.
        ...(line.amountOriginal != null && line.amountOriginalCurrency
          ? {
              amountOriginal: String(
                Math.sign(signed) * Math.abs(Number(line.amountOriginal)),
              ),
              amountOriginalCurrency: line.amountOriginalCurrency,
            }
          : {}),
        description: line.description || undefined,
      };
    })
    .filter((line): line is NonNullable<typeof line> => line != null);

  const documentIdentifiers = (resolved.documentOptions ?? [])
    .map((option) => option.document?.identifier)
    .filter((identifier): identifier is string => !!identifier);

  const transactionIdentifiers = [
    ...new Set(
      [
        ...(resolved.selectedTransactions ?? [])
          .map((transaction) => transaction.identifier)
          .filter((identifier): identifier is string => !!identifier),
        ...(resolved.linesSuggestion?.ruleSuggestionTransactionOptions ?? [])
          .map((option) => option.transaction?.identifier)
          .filter((identifier): identifier is string => !!identifier),
      ].filter(Boolean),
    ),
  ];

  const categoryIdentifier = resolved.categoryOption?.category?.identifier;

  const links =
    documentIdentifiers.length > 0 ||
    transactionIdentifiers.length > 0 ||
    categoryIdentifier ||
    newCategoryName
      ? {
          ...(documentIdentifiers.length > 0 ? { documentIdentifiers } : {}),
          ...(transactionIdentifiers.length > 0
            ? { transactionIdentifiers }
            : {}),
          ...(categoryIdentifier
            ? { categoryIdentifier }
            : newCategoryName
              ? { newCategoryName }
              : {}),
        }
      : undefined;

  return {
    transactionIdentifier,
    lines,
    ...(links ? { links } : {}),
    ...(settlementId ? { settlementEntityIdentifier: settlementId } : {}),
    ...(payerTagIdentifiers.length > 0 ? { payerTagIdentifiers } : {}),
    ...(settlementTagIdentifiers.length > 0
      ? { settlementTagIdentifiers }
      : {}),
  };
}

export function resolveRowTabJournalResolved(
  item: TriageListItemFragment,
  tab: TriageResolutionTab,
  detailTriage: TriageDetail | null,
): JournalResolved {
  if (tab === "suggestions") {
    const linesSuggestion =
      detailTriage?.suggestions?.find(
        (suggestion) => (suggestion.lines?.length ?? 0) > 0,
      ) ?? null;
    return {
      categoryOption: null,
      documentOption: null,
      documentOptions: [],
      chartedAccountOption: null,
      selectedTransactions: [],
      linesSuggestion,
    };
  }

  const suggestions = (detailTriage?.suggestions ??
    []) as TriageSuggestionStub[];
  const documentSuggestion =
    suggestions.find((suggestion) => suggestion.job === "MATCH_DOCUMENT") ??
    null;
  const categorizeSuggestion =
    suggestions.find((suggestion) => suggestion.job === "CATEGORIZE") ?? null;

  if (tab === "document") {
    const linkedDocument = detailTriage?.paymentFor?.[0];
    if (linkedDocument?.id) {
      const linkedOption = {
        id: linkedDocument.id,
        document: linkedDocument,
      } as JournalResolved["documentOption"];
      return {
        categoryOption: null,
        documentOption: linkedOption,
        documentOptions: linkedOption ? [linkedOption] : [],
        chartedAccountOption: null,
        selectedTransactions: [],
        linesSuggestion: null,
      };
    }

    const documentOption =
      documentSuggestion?.ruleSuggestionDocumentOptions?.[0] ?? null;
    return {
      categoryOption: null,
      documentOption,
      documentOptions: documentOption ? [documentOption] : [],
      chartedAccountOption: null,
      selectedTransactions: [],
      linesSuggestion: null,
    };
  }

  if (tab === "transactions") {
    return {
      categoryOption: null,
      documentOption: null,
      documentOptions: [],
      chartedAccountOption: null,
      selectedTransactions: [],
      linesSuggestion: null,
    };
  }

  const chartedAccountOption =
    categorizeSuggestion?.ruleSuggestionChartedAccountOption ?? null;

  return {
    categoryOption: null,
    documentOption: null,
    documentOptions: [],
    chartedAccountOption,
    selectedTransactions: [],
    linesSuggestion: null,
  };
}

const MAX_VISIBLE_JOURNAL_ROWS = 10;
const JOURNAL_LOADING_ROW_COUNT = 2;
// The app's own row, not the compact one: this is the entry about to be
// booked, read and edited at the size every other table is.
const TRIAGE_JOURNAL_ROW_HEIGHT = Values.DEFAULT_ROW_HEIGHT;

/**
 * Column weights for the narrow match panel. Every column is a percentage so
 * the table fits the panel: one pixel-width column makes the table claim the
 * sum of its columns' pixel widths and scroll sideways, and the pinned actions
 * column then covers the amounts. Document only shows when a line has one.
 */
const TRIAGE_JOURNAL_COLUMN_WEIGHTS: Record<string, number> = {
  description: 27,
  chartedAccount: 27,
  document: 20,
  amountOriginal: 22,
  debit: 23,
  credit: 23,
  tax: 18,
  amountTax: 18,
};

function journalTableHeight(lineCount: number): number {
  const visibleDataRows = Math.min(lineCount, MAX_VISIBLE_JOURNAL_ROWS);
  return (
    TRIAGE_JOURNAL_ROW_HEIGHT +
    visibleDataRows * TRIAGE_JOURNAL_ROW_HEIGHT +
    TRIAGE_JOURNAL_ROW_HEIGHT +
    TRIAGE_JOURNAL_ROW_HEIGHT
  );
}

export function TriageJournalEntryTable({
  triage,
  resolved = EMPTY_TRIAGE_JOURNAL_RESOLVED,
  lines,
  isLoading = false,
  onRemoveLineSource,
  settlement = null,
}: {
  /** The payment the entry books - or, given ``lines``, whose business's accounts they are. */
  triage: TriageJournalSource;
  resolved?: JournalResolved;
  /**
   * The entry as given rather than built from the payment - one business's
   * side of a settlement - and read, not edited. A line with no account and
   * no amount is a blank row, keeping its place across from another entry.
   */
  lines?: JournalEntryLine[];
  isLoading?: boolean;
  /** Undo the selection a built row stands for (document, peer, suggestion, coding). */
  onRemoveLineSource?: (source: TriageJournalLineSource) => void;
  /** Booked in another business too: its entry with that business's side, to see. */
  settlement?: TriageJournalSettlementContext | null;
}) {
  const colors = useLayoutColors();
  const gridColors = useResolvedGridAppearance({
    rowBackground: colors.main,
    headerBackground: colors.main,
    cellBackground: colors.main,
    rowHoverBackground: "var(--muted)",
  });
  const { lineItems: documentLineItems, loading: documentLinesLoading } =
    useTriageDocumentLineItems(resolved);
  const builtLines = useMemo(
    () =>
      lines ??
      buildTriageJournalEntryLines(
        triage,
        resolved,
        documentLineItems,
        settlement,
      ),
    [documentLineItems, lines, resolved, settlement, triage],
  );
  const builtLinesKey = useMemo(
    () =>
      builtLines
        .map((line, index) => builtJournalLineKey(line, index))
        .join("|"),
    [builtLines],
  );

  const [manualLines, setManualLines] = useState<JournalEntryLine[]>([]);
  // Drop manual rows during render — clearing them in an effect paints the new
  // built lines alongside the previous row's manual lines for one frame.
  const [manualLinesKey, setManualLinesKey] = useState(builtLinesKey);
  if (manualLinesKey !== builtLinesKey) {
    setManualLinesKey(builtLinesKey);
    setManualLines([]);
  }

  const tableLines = useMemo(
    () => [
      ...builtLines.map((line, index) => ({
        ...line,
        metadata: {
          _frontend_uuid: builtJournalLineKey(line, index),
        },
      })),
      ...manualLines.map((line) => ({
        ...line,
        metadata: {
          ...(line.metadata ?? {}),
          ...TRIAGE_MANUAL_JOURNAL_LINE_METADATA,
          _frontend_uuid: line.metadata?._frontend_uuid ?? crypto.randomUUID(),
        },
      })),
    ],
    [builtLines, manualLines],
  );

  const handleLinesChange = useCallback((value: unknown) => {
    const rows = Array.isArray(value) ? value : [];
    setManualLines(
      rows
        .filter((row) =>
          isTriageManualJournalLine(row as Record<string, unknown>),
        )
        .map((row) => row as JournalEntryLine),
    );
  }, []);

  const createManualJournalLine = useCallback(
    () => ({
      metadata: {
        _frontend_uuid: crypto.randomUUID(),
        ...TRIAGE_MANUAL_JOURNAL_LINE_METADATA,
      },
      description: "",
      debit: 0,
      credit: 0,
      chartedAccount: { name: "" },
      document: null,
    }),
    [],
  );

  const isRowMutable = useCallback(
    (row: Record<string, unknown>) => isTriageManualJournalLine(row),
    [],
  );

  // Everything except the triage bank leg can be removed: manual rows drop out
  // of the table, built rows undo the selection that produced them.
  const isRowDeletable = useCallback((row: Record<string, unknown>) => {
    if (isTriageManualJournalLine(row)) {
      return true;
    }
    const source = row.source as TriageJournalLineSource | undefined;
    return source != null && source.kind !== "bank";
  }, []);

  const handleDeleteRow = useCallback(
    (row: Record<string, unknown>) => {
      const source = row.source as TriageJournalLineSource | undefined;
      if (!source || source.kind === "bank") {
        return;
      }
      onRemoveLineSource?.(source);
    },
    [onRemoveLineSource],
  );

  const valueOptions = useMemo(() => {
    if (!journalEntryLinesField || journalEntryLinesField.type !== "table") {
      return { columns: [] as TableColumnType[] };
    }

    const options = (journalEntryLinesField.getValueOptions?.({
      entity: triage.entity,
      journalEntry: { transactions: tableLines },
    } as unknown as Parameters<
      NonNullable<typeof journalEntryLinesField.getValueOptions>
    >[0]) ?? { columns: [] }) as { columns: TableColumnType[] };

    const hasDocument = tableLines.some((line) => line.document != null);
    // The tax on a line beside it, as an entry is written by hand, where a
    // line has one.
    const taxColumn =
      handWrittenLinesField?.type === "table" &&
      tableLines.some((line) => line.tax != null)
        ? (
            handWrittenLinesField.getValueOptions?.({} as never) as
              | { columns: TableColumnType[] }
              | undefined
          )?.columns.find((column) => column.objectKey === "tax")
        : undefined;
    const shown = options.columns.filter(
      (column) =>
        column.objectKey in TRIAGE_JOURNAL_COLUMN_WEIGHTS &&
        (column.objectKey !== "document" || hasDocument) &&
        // What a line was on its document in another currency, only where
        // one was.
        (column.objectKey !== "amountOriginal" ||
          tableLines.some((line) => line.amountOriginal != null)),
    );
    const columns = [
      ...shown.filter(
        (column) => column.type !== "currency" && column.type !== "number",
      ),
      ...(taxColumn
        ? [
            taxColumn,
            // And how much of it: booked on the line's own side, beside it -
            // its debit or credit and this come to what the totals say.
            {
              label: "Tax amount",
              type: "currency" as const,
              headerFieldType: "number" as const,
              objectKey: "amountTax",
              isDisabled: true,
              isSearchable: false,
              getValue: (row: { amountTax?: string | number | null }) =>
                row?.amountTax != null && Number(row.amountTax) !== 0
                  ? String(Math.abs(Number(row.amountTax)))
                  : "",
            } as TableColumnType,
          ]
        : []),
      ...shown.filter(
        (column) => column.type === "currency" || column.type === "number",
      ),
    ];
    const totalWeight = columns.reduce(
      (sum, column) => sum + TRIAGE_JOURNAL_COLUMN_WEIGHTS[column.objectKey],
      0,
    );
    return {
      columns: columns.map((column) => ({
        ...column,
        width:
          (TRIAGE_JOURNAL_COLUMN_WEIGHTS[column.objectKey] * 100) / totalWeight,
        widthMode: "percentage" as const,
      })),
    };
  }, [tableLines, triage.entity]);

  const { totalDebit, totalCredit, isBalanced } = useMemo(
    () => journalEntryTotals(tableLines),
    [tableLines],
  );

  // Data rows: Description | Account | Document | Debit | Credit.
  // Summary: Total | (empty) | (empty) | Debit | Credit — chips green when balanced.
  const summaryRow = useMemo(() => {
    const summaryChipColor = isBalanced ? EnumColors.GREEN : EnumColors.RED;
    return {
      description: "Total",
      debit: (
        <div className="flex w-full min-w-0 justify-end items-center h-11 pl-2 pr-2">
          <SimpleEnum
            label={Utils.fCurrency(totalDebit, false, true)}
            enumColor={summaryChipColor}
            size="md"
          />
        </div>
      ),
      credit: (
        <div className="flex w-full min-w-0 justify-end items-center h-11 pl-2 pr-2">
          <SimpleEnum
            label={Utils.fCurrency(totalCredit, false, true)}
            enumColor={summaryChipColor}
            size="md"
          />
        </div>
      ),
    };
  }, [isBalanced, totalCredit, totalDebit]);

  // A suggestion paying a document in another currency: what it was turned
  // from, and at what rate - under the entry, which is all in the payment's.
  const converted = (
    resolved.linesSuggestion?.ruleSuggestionDocumentOptions ?? []
  ).find(
    (option) =>
      option.amountDocumentCurrency &&
      option.amountDocumentCurrency !== option.amountTransactionCurrency &&
      Number(option.amountDocument),
  );

  const lineCount = tableLines.length;
  const showLoading =
    (isLoading || documentLinesLoading) && builtLines.length === 0;
  const scrollable = !showLoading && lineCount > MAX_VISIBLE_JOURNAL_ROWS;
  const computedHeight = journalTableHeight(lineCount);

  const table = (
    <FieldTable
      key={builtLinesKey}
      value={tableLines as never}
      valueOptions={valueOptions}
      // Lines given are read: nothing added, changed or taken away.
      {...(lines
        ? {}
        : {
            events: { onChangeField: handleLinesChange },
            isRowMutable,
            isRowDeletable,
            onDeleteRow: handleDeleteRow,
            createRow: createManualJournalLine,
          })}
      isLoading={showLoading}
      loadingRowCount={JOURNAL_LOADING_ROW_COUNT}
      showHeaderWhenEmpty
      summaryRow={summaryRow}
      overlayScrollbars
      rowHeight={TRIAGE_JOURNAL_ROW_HEIGHT}
      cellSize="md"
      style={{
        width: "100%",
        padding: 0,
        ...(scrollable ? { height: computedHeight } : {}),
        ["--field-table-cell-bg" as string]: gridColors.cellBackground,
        ["--field-table-header-bg" as string]: gridColors.headerBackground,
        ["--field-table-header-color" as string]: "var(--muted-foreground)",
        ["--field-table-row-hover-bg" as string]: gridColors.rowHoverBackground,
      }}
    />
  );
  if (!converted) {
    return table;
  }
  return (
    <>
      {table}
      <Typography
        level="body-xs"
        data-testid="triage-journal-converted"
        className="text-muted-foreground/80 pl-4 pr-4 pt-1.5 pb-1.5"
      >
        Converted from{" "}
        {Utils.fCurrency(
          Number(converted.amountDocument),
          false,
          true,
          converted.amountDocumentCurrency,
        )}{" "}
        at 1 {converted.amountDocumentCurrency} ={" "}
        {(
          Number(converted.amountTransaction) / Number(converted.amountDocument)
        ).toFixed(4)}{" "}
        {converted.amountTransactionCurrency}
      </Typography>
    </>
  );
}
